import { useMemo, useState } from 'react'
import { periodDates, periodNumberFor } from '../../lib/payPeriods'

export type ReportPreset = 'this' | 'next' | 'last-six' | 'custom'

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function periodBounds(periodNumber: number): { from: string; to: string } {
  const dates = periodDates(periodNumber)
  return { from: dates[0].date, to: dates[dates.length - 1].date }
}

function rangeForPreset(preset: Exclude<ReportPreset, 'custom'>): { from: string; to: string } {
  const current = periodNumberFor(todayIso())
  if (preset === 'this') return periodBounds(current)
  if (preset === 'next') return periodBounds(current + 1)
  return { from: periodBounds(current - 5).from, to: periodBounds(current).to }
}

/**
 * The date range every report shares (docs/product/reports.md "Shared
 * controls"): the presets for this pay period, the next, and the last six, a
 * pay-period selector, and two dates that make the range custom when edited.
 */
export function useReportRange() {
  const initial = rangeForPreset('this')
  const [preset, setPreset] = useState<ReportPreset>('this')
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)

  const periodChoices = useMemo(() => {
    const current = periodNumberFor(todayIso())
    return [current + 1, current, current - 1, current - 2, current - 3, current - 4, current - 5]
  }, [])
  const selectedPeriod = periodChoices.find((number) => {
    const bounds = periodBounds(number)
    return bounds.from === from && bounds.to === to
  })

  return {
    preset,
    from,
    to,
    periodChoices,
    selectedPeriod,
    invalid: to < from,
    applyPreset(next: ReportPreset) {
      setPreset(next)
      if (next === 'custom') return
      const range = rangeForPreset(next)
      setFrom(range.from)
      setTo(range.to)
    },
    applyPeriod(number: number) {
      const bounds = periodBounds(number)
      setPreset('custom')
      setFrom(bounds.from)
      setTo(bounds.to)
    },
    setFrom(value: string) {
      setPreset('custom')
      setFrom(value)
    },
    setTo(value: string) {
      setPreset('custom')
      setTo(value)
    },
  }
}

export type ReportRange = ReturnType<typeof useReportRange>
